/* src/context/AuthContext.jsx - Última Modificación: 08-01-2026 */
/* ----------------- única autoridad de sesión ----------------- */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState('active'); // active | expired | logged_out

  const navigate = useNavigate();

  /* 🔁 Cargar sesión inicial desde sessionStorage */
  useEffect(() => {
    const storedToken = sessionStorage.getItem('authToken');

    if (storedToken) {
      try {
        const decoded = jwtDecode(storedToken);
        const now = Date.now() / 1000;

        if (decoded.exp > now) {
          setUser({
            id: decoded.id,
            usuario: decoded.usuario,
            nombre: decoded.nombre,
            rol: decoded.rol,
            tipo: decoded.tipo,
          });
        } else {
          sessionStorage.removeItem('authToken');
        }
      } catch (error) {
        console.error('Token inválido:', error);
        sessionStorage.removeItem('authToken');
      }
    }

    setLoading(false);
  }, []);

  /* 🧠 LOGOUT MULTI-TAB (si una pestaña cierra, todas cierran) */
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'authToken' && !event.newValue) {
        setUser(null);
        setAuthStatus('logged_out');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /* 🔐 LOGIN CENTRALIZADO */
  const login = useCallback((newToken) => {
    sessionStorage.setItem('authToken', newToken);

    const decoded = jwtDecode(newToken);

    setUser({
      id: decoded.id,
      usuario: decoded.usuario,
      nombre: decoded.nombre,
      rol: decoded.rol,
      tipo: decoded.tipo,
    });

    setAuthStatus('active');
  }, []);

  /* ⛔ LOGOUT CENTRALIZADO (decisión humana) */
  const logout = useCallback(() => {
    sessionStorage.removeItem('authToken');
    setUser(null);
    setAuthStatus('logged_out');
    navigate('/login', { replace: true });
  }, [navigate]);

  /* ⏳ EXPIRAR SESIÓN (decisión del sistema) */
  const expireSession = useCallback(() => {
    setAuthStatus('expired');
  }, []);

  /* 🔁 Cuando la sesión expira, se ejecuta el logout real */
  useEffect(() => {
    if (authStatus === 'expired') {
      logout();
    }
  }, [authStatus, logout]);

  /* 🔐 Roles */
  const hasRole = useCallback(
    (roles = []) => Boolean(user && roles.includes(user.rol)),
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        expireSession,
        hasRole,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/* 🔗 Hook seguro */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};
